import { createTheme } from "@mui/material/styles";

export const scrollbarStyles = {
	"&::-webkit-scrollbar": {
		width: "8px",
		height: "8px",
	},
	"&::-webkit-scrollbar-thumb": {
		borderRadius: "8px",
		backgroundColor: "rgba(153, 0, 0, 0.35)",
	},
	"&::-webkit-scrollbar-track": {
		backgroundColor: "rgba(0, 0, 0, 0.04)",
	},
};

const buildShadows = baseColor => {
	const shadows = [
		"none",
		`0 1px 2px ${baseColor}0a`,
		`0 2px 8px ${baseColor}10`,
		`0 4px 16px ${baseColor}14`,
		`0 8px 24px ${baseColor}1a`,
		`0 12px 32px ${baseColor}1f`,
		`0 16px 40px ${baseColor}24`,
		`0 20px 48px ${baseColor}29`,
		`0 24px 56px ${baseColor}2e`,
		`0 28px 64px ${baseColor}33`,
	];
	while (shadows.length < 25) {
		shadows.push(`0 32px 72px ${baseColor}38`);
	}
	return shadows;
};

const lightPalette = {
	mode: "light",
	primary: {
		main: "#990000",
		light: "#cc2222",
		dark: "#660000",
		contrastText: "#ffffff",
	},
	secondary: {
		main: "#1f2937",
		light: "#374151",
		dark: "#111827",
	},
	background: {
		default: "#f3f4f6",
		paper: "#ffffff",
	},
	text: {
		primary: "#111827",
		secondary: "#6b7280",
	},
	divider: "rgba(17, 24, 39, 0.08)",
};

const darkPalette = {
	mode: "dark",
	primary: {
		main: "#e23b3b",
		light: "#ff6f60",
		dark: "#990000",
		contrastText: "#ffffff",
	},
	secondary: {
		main: "#cbd5e1",
		light: "#e2e8f0",
		dark: "#94a3b8",
	},
	background: {
		default: "#0b0f17",
		paper: "#141a24",
	},
	text: {
		primary: "#e5e7eb",
		secondary: "#9ca3af",
	},
	divider: "rgba(255, 255, 255, 0.1)",
};

const buildThemeOptions = mode => {
	const isDark = mode === "dark";
	const palette = isDark ? darkPalette : lightPalette;
	const borderColor = isDark
		? "rgba(255, 255, 255, 0.1)"
		: "rgba(17, 24, 39, 0.08)";
	const shadowBase = isDark ? "#000000" : "#111827";

	return {
		scrollbarStyles,
		palette,
		shape: {
			borderRadius: 12,
		},
		typography: {
			fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
			h4: { fontWeight: 700, letterSpacing: "-0.02em" },
			h5: { fontWeight: 700, letterSpacing: "-0.01em" },
			h6: { fontWeight: 600 },
			subtitle1: { fontWeight: 600 },
			button: { textTransform: "none", fontWeight: 600 },
		},
		shadows: buildShadows(shadowBase),
		components: {
			MuiCssBaseline: {
				styleOverrides: {
					body: {
						backgroundColor: palette.background.default,
					},
				},
			},
			MuiButton: {
				defaultProps: {
					disableElevation: true,
				},
				styleOverrides: {
					root: {
						borderRadius: 10,
						padding: "8px 16px",
					},
					containedPrimary: {
						background: isDark
							? "linear-gradient(135deg, #990000 0%, #e23b3b 55%, #ff6f60 100%)"
							: "linear-gradient(135deg, #660000 0%, #990000 55%, #cc2222 100%)",
					},
				},
			},
			MuiPaper: {
				defaultProps: {
					elevation: 0,
				},
				styleOverrides: {
					rounded: {
						borderRadius: 16,
						border: `1px solid ${borderColor}`,
					},
				},
			},
			MuiCard: {
				styleOverrides: {
					root: {
						borderRadius: 16,
						border: `1px solid ${borderColor}`,
						boxShadow: isDark
							? "0 4px 16px rgba(0, 0, 0, 0.4)"
							: "0 4px 16px rgba(17, 24, 39, 0.06)",
					},
				},
			},
			MuiAppBar: {
				styleOverrides: {
					root: {
						boxShadow: `0 1px 0 ${borderColor}`,
					},
				},
			},
			MuiDrawer: {
				styleOverrides: {
					paper: {
						borderRight: `1px solid ${borderColor}`,
						backgroundColor: palette.background.paper,
					},
				},
			},
			MuiListItemButton: {
				styleOverrides: {
					root: {
						borderRadius: 10,
						margin: "2px 8px",
						"&.Mui-selected": {
							backgroundColor: isDark
								? "rgba(226, 59, 59, 0.18)"
								: "rgba(153, 0, 0, 0.1)",
							color: isDark ? "#ff6f60" : "#990000",
							"&:hover": {
								backgroundColor: isDark
									? "rgba(226, 59, 59, 0.24)"
									: "rgba(153, 0, 0, 0.14)",
							},
						},
					},
				},
			},
			MuiTextField: {
				defaultProps: {
					variant: "outlined",
					size: "small",
				},
			},
			MuiOutlinedInput: {
				styleOverrides: {
					root: {
						borderRadius: 10,
					},
				},
			},
			MuiDialog: {
				styleOverrides: {
					paper: {
						borderRadius: 16,
					},
				},
			},
			MuiTableCell: {
				styleOverrides: {
					head: {
						fontWeight: 700,
						backgroundColor: isDark ? "#1b2330" : "#fafafa",
					},
				},
			},
			MuiChip: {
				styleOverrides: {
					root: {
						borderRadius: 8,
					},
				},
			},
		},
	};
};

export const createAppTheme = (locale, mode = "light") =>
	createTheme(buildThemeOptions(mode), locale);

export default createAppTheme;
